import { useStore } from 'zustand';

import { authSession } from '@units/auth/service/stores';
import { type SessionState } from '@units/auth/types';

/**
 * Who this tab is, for a component that only wants to know.
 *
 * The twin of `useBootstrapSession`, minus the half that acts: it subscribes to the session and
 * starts nothing. That distinction is the point of having two hooks rather than one. `POST
 * /auth/refresh` rotates the refresh token, so a second exchange presenting the spent one is
 * indistinguishable from a stolen cookie and costs the user every device
 * (`units/auth/lib/session-refresh.util.ts`); starting it is therefore a decision, and it belongs to
 * the composition root, which is the only place that can hold the route tree back until the answer
 * lands (`app/app.component.tsx`).
 *
 * Everything below it — a badge in the header, a menu that names the signed-in person — reads. A
 * reader that called `bootstrap()` would still look right in a browser, where the rotation succeeds
 * and the memo holds; what it breaks is every environment where it does not, because an
 * `unavailable` rotation deliberately releases the memo so the tab can try again, and a reader that
 * asks on every render turns «try again» into «ask for ever».
 *
 * `useStore` with a selector, like the hook next to it: without one the subscription is to the whole
 * state, which `rules/frontend-fsd.mdc` rule 16 asks against for the reason this store was shaped
 * around — its readers are woken by a session change and by nothing else.
 */
export const useSession = (): SessionState => useStore(authSession, (state) => state.session);
